(function () {
  if (window.orderNowProxyInitialized) return;
  window.orderNowProxyInitialized = true;

  const activeButtons = new Set();
  const usingPubSub = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  const skipCartRestore = new Map();

  const fireCompletionEvent = (success, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent('order-now-proxy:complete', {
        detail: {
          source: detail.source || 'order-now-proxy',
          success,
          triggerRole: detail.triggerRole || undefined,
        },
      })
    );
  };

  const resetButton = (button) => {
    if (!button) return;
    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');
    button.style.removeProperty('--spinner-color');
    button.style.removeProperty('--loading-text-color');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
      spinner.style.removeProperty('--spinner-color');
    }
  };

  const markLoading = (button) => {
    button.setAttribute('aria-disabled', 'true');
    const spinner = button.querySelector('.loading__spinner');
    const computedStyles = window.getComputedStyle(button);
    const spinnerColor = computedStyles?.color;
    if (spinnerColor && spinnerColor !== 'transparent' && spinnerColor !== 'rgba(0, 0, 0, 0)') {
      button.style.setProperty('--spinner-color', spinnerColor);
      button.style.setProperty('--loading-text-color', spinnerColor);
      if (spinner) {
        spinner.style.setProperty('--spinner-color', spinnerColor);
      }
    }
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');
  };

  const cleanupActiveButtons = () => {
    activeButtons.forEach((button) => resetButton(button));
    activeButtons.clear();
    const restoreTargets = Array.from(skipCartRestore.entries());
    restoreTargets.forEach(([button, originalValue]) => {
      if (!button) return;
      if (originalValue === null || originalValue === undefined) {
        delete button.dataset.skipCart;
      } else {
        button.dataset.skipCart = originalValue;
      }
      button.removeAttribute('data-order-now-proxy-skip');
    });
    skipCartRestore.clear();
  };

  if (usingPubSub) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, cleanupActiveButtons);
    subscribe(PUB_SUB_EVENTS.cartError, cleanupActiveButtons);
  }
  document.addEventListener('order-now-proxy:complete', cleanupActiveButtons);

  /**
   * Checks free shipping modal before proceeding with order
   * @param {number} quantity - Quantity to order
   * @param {boolean} skipCheckout - Whether skip checkout is enabled
   * @param {Function} callback - Called with final quantity
   */
  const checkFreeShippingAndProceed = async (quantity, skipCheckout, callback) => {
    // Only check if skip checkout is enabled and modal function exists
    if (!skipCheckout || typeof window.checkFreeShippingBeforeOrder !== 'function') {
      callback(quantity);
      return;
    }

    window.checkFreeShippingBeforeOrder(quantity, callback);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-command-now-proxy]');
    if (!trigger) return;

    event.preventDefault();
    if (trigger.getAttribute('aria-disabled') === 'true') return;

    const formSelector = trigger.dataset.targetForm;
    const formElement = resolveTargetForm(formSelector);
    const skipCheckout = trigger.dataset.skipCheckout === 'true';

    // Get quantity from trigger or form
    let quantity = parseInt(trigger.dataset.defaultQuantity || '1', 10) || 1;

    // If there's a form, try to get quantity from it
    if (formElement) {
      const quantityInput = formElement.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantity = parseInt(quantityInput.value, 10) || 1;
      }
    }

    // Check free shipping modal before proceeding
    checkFreeShippingAndProceed(quantity, skipCheckout, (finalQuantity) => {
      // Update quantity on trigger
      trigger.dataset.defaultQuantity = finalQuantity;

      markLoading(trigger);
      activeButtons.add(trigger);

      if (formElement) {
        const commandButton =
          formElement.querySelector('button[data-product-form-role="command-now"]') ||
          formElement.querySelector('.command-now-button');
        
        // Update quantity input in form if it exists
        const quantityInput = formElement.querySelector('input[name="quantity"]');
        if (quantityInput && finalQuantity !== parseInt(quantityInput.value, 10)) {
          quantityInput.value = finalQuantity;
          // Trigger change event for any listeners
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        if (commandButton) {
          if (skipCheckout) {
            if (!skipCartRestore.has(commandButton)) {
              skipCartRestore.set(commandButton, commandButton.dataset.skipCart);
            }
            commandButton.dataset.skipCart = 'true';
            commandButton.setAttribute('data-order-now-proxy-skip', 'true');
          }
          if (typeof formElement.requestSubmit === 'function') {
            formElement.requestSubmit(commandButton);
          } else {
            commandButton.click();
          }
          return;
        }
      }

      const variantId = trigger.dataset.variantId;
      if (!variantId) {
        console.warn('Order now proxy: no target form or variant id provided.');
        resetButton(trigger);
        activeButtons.delete(trigger);
        fireCompletionEvent(false, { triggerRole: 'proxy-missing-target' });
        return;
      }

      submitDirect(trigger, variantId, finalQuantity, { skipCheckout }).catch(() => {
        if (!usingPubSub) {
          resetButton(trigger);
          activeButtons.delete(trigger);
        }
      });
    });
  });

  function resolveTargetForm(selector) {
    if (!selector) return null;
    if (selector.startsWith('#') || selector.startsWith('.')) {
      return document.querySelector(selector);
    }
    return document.getElementById(selector);
  }

  function submitDirect(button, variantId, quantity, options = {}) {
    const skipCheckout = options.skipCheckout === true;
    if (typeof fetchConfig !== 'function' || !window.routes) {
      console.warn('Order now proxy: required theme helpers are missing, falling back to cart page.');
      window.location = '/cart';
      cleanupActiveButtons();
      return Promise.reject();
    }

    const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      try {
        formData.append(
          'sections',
          cartElement
            .getSectionsToRender()
            .map((section) => section.id)
        );
      } catch (error) {
        console.warn('Order now proxy: unable to resolve cart sections.', error);
      }
      formData.append('sections_url', window.location.pathname);
      if (typeof cartElement.setActiveElement === 'function') {
        cartElement.setActiveElement(button);
      }
    }

    config.body = formData;

    return fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (usingPubSub && typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'order-now-proxy',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          throw response;
        }

        if (!cartElement) {
          cleanupActiveButtons();
          fireCompletionEvent(true);
          window.location = skipCheckout ? '/checkout' : window.routes.cart_url;
          return response;
        }

        if (usingPubSub && typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'order-now-proxy',
            productVariantId: variantId,
            cartData: response,
          });
        }

        if (cartElement.classList && cartElement.classList.contains('is-empty')) {
          cartElement.classList.remove('is-empty');
        }
        const innerDrawer = cartElement.querySelector && cartElement.querySelector('.drawer__inner');
        if (innerDrawer && innerDrawer.classList.contains('is-empty')) {
          innerDrawer.classList.remove('is-empty');
        }

        if (skipCheckout) {
          cleanupActiveButtons();
          fireCompletionEvent(true, { triggerRole: 'proxy-skip-checkout' });
          window.location = '/checkout';
        } else {
          if (typeof cartElement.renderContents === 'function') {
            cartElement.renderContents(response);
          }

          cleanupActiveButtons();
          fireCompletionEvent(true);
        }

        return response;
      })
      .catch((error) => {
        console.error('Order now proxy failure', error);
        if (usingPubSub && typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'order-now-proxy',
            productVariantId: variantId,
            errors: error && error.errors ? error.errors : error,
          });
        }
        cleanupActiveButtons();
        fireCompletionEvent(false, { triggerRole: skipCheckout ? 'proxy-skip-checkout' : undefined });
        throw error;
      });
  }
})();
